import { useState } from 'react'
import { Plus, Phone, Mail, User, Pencil, PowerOff, Power, Package, Check, Trash2, ShoppingBag, BookOpen, Search, X, ChevronDown } from 'lucide-react'
import { PageSpinner } from '@/components/ui/Spinner'
import { useSuppliers, useSaveSupplier, useIngredients, useSupplierCatalog, useSaveSupplierCatalogItem, useDeleteSupplierCatalogItem, useAllSupplierCatalog } from '@/hooks/useMenu'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import type { Supplier, Ingredient, SupplierCatalogItem } from '@/types/app'

const BLANK: Partial<Supplier> = {
  name: '', contact_name: '', phone: '', email: '', is_active: true,
}

// Ingredient extended with junction data
type IngredientWithLinks = Ingredient & {
  ingredient_suppliers: Array<{
    id: string
    supplier_id: string
    is_preferred: boolean
    unit_price: number | null
    notes: string | null
  }>
}

function SupplierForm({
  initial,
  onDone,
  onCancel,
}: {
  initial: Partial<Supplier>
  onDone: () => void
  onCancel: () => void
}) {
  const { mutateAsync, isPending } = useSaveSupplier()
  const [form, setForm] = useState({ ...BLANK, ...initial })
  const [error, setError] = useState<string | null>(null)

  function f(key: keyof typeof form, val: string | boolean) {
    setForm(p => ({ ...p, [key]: val }))
  }

  async function save() {
    if (!form.name?.trim()) { setError('Name is required'); return }
    setError(null)
    try {
      await mutateAsync(form)
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  return (
    <div className="bg-white rounded-xl border border-brand-200 p-4 space-y-3 shadow-sm">
      <h3 className="text-sm font-semibold text-stone-800">{form.id ? 'Edit Supplier' : 'New Supplier'}</h3>
      {error && <p className="text-xs text-danger bg-red-50 px-2 py-1.5 rounded">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div className="flex flex-col gap-1 sm:col-span-2">
          <label className="text-xs font-medium text-stone-600">Supplier Name *</label>
          <input
            value={form.name ?? ''}
            onChange={e => f('name', e.target.value)}
            placeholder="e.g. Sweets & Co."
            className="border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-stone-600">Contact Name</label>
          <input
            value={form.contact_name ?? ''}
            onChange={e => f('contact_name', e.target.value)}
            placeholder="e.g. Raj Kumar"
            className="border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-stone-600">Phone</label>
          <input
            value={form.phone ?? ''}
            onChange={e => f('phone', e.target.value)}
            placeholder="+1 (555) 000-0000"
            inputMode="tel"
            className="border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <label className="text-xs font-medium text-stone-600">Email</label>
          <input
            value={form.email ?? ''}
            onChange={e => f('email', e.target.value)}
            placeholder="supplier@example.com"
            type="email"
            className="border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={save}
          disabled={isPending}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          <Check size={14} />
          {isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm text-stone-500 hover:bg-stone-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Catalog section ──────────────────────────────────────────────────────────

const BLANK_CATALOG = { item_name: '', unit: '', unit_price: '', notes: '' }

function CatalogSection({ supplierId, isOwner }: { supplierId: string; isOwner: boolean }) {
  const { data: items = [], isLoading } = useSupplierCatalog(supplierId)
  const { mutateAsync: save }           = useSaveSupplierCatalogItem()
  const { mutateAsync: del }            = useDeleteSupplierCatalogItem()

  const [adding, setAdding]     = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm]         = useState(BLANK_CATALOG)
  const [saving, setSaving]     = useState(false)

  function startAdd() { setForm(BLANK_CATALOG); setEditingId(null); setAdding(true) }
  function startEdit(item: SupplierCatalogItem) {
    setForm({
      item_name: item.item_name,
      unit: item.unit ?? '',
      unit_price: item.unit_price != null ? String(item.unit_price) : '',
      notes: item.notes ?? '',
    })
    setEditingId(item.id)
    setAdding(true)
  }
  function cancelAdd() { setAdding(false); setEditingId(null) }

  async function handleSave() {
    if (!form.item_name.trim()) return
    setSaving(true)
    await save({
      id: editingId ?? undefined,
      supplier_id: supplierId,
      item_name: form.item_name.trim(),
      unit: form.unit.trim() || null,
      unit_price: form.unit_price ? parseFloat(form.unit_price) : null,
      notes: form.notes.trim() || null,
    })
    setSaving(false)
    setAdding(false)
    setEditingId(null)
  }

  async function toggleProcured(item: SupplierCatalogItem) {
    await save({ id: item.id, supplier_id: supplierId, item_name: item.item_name, is_procured: !item.is_procured })
  }

  if (isLoading) return null

  const procured  = items.filter(i => i.is_procured)
  const available = items.filter(i => !i.is_procured)

  return (
    <div className="border-t border-stone-100">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-stone-50/70">
        <div className="flex items-center gap-2">
          <BookOpen size={13} className="text-stone-400" />
          <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Supplier Catalog</span>
          {items.length > 0 && (
            <span className="text-2xs text-stone-400">
              {procured.length} procuring · {available.length} available
            </span>
          )}
        </div>
        <button
          onClick={startAdd}
          className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium px-2 py-1 rounded hover:bg-brand-50 transition-colors"
        >
          <Plus size={12} /> Add item
        </button>
      </div>

      {/* Add / Edit form */}
      {adding && (
        <div className="px-4 py-3 border-b border-brand-100 bg-brand-50/40 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              autoFocus
              placeholder="Item name *"
              value={form.item_name}
              onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
              className="col-span-2 border border-stone-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <input
              placeholder="Unit (kg, piece, dozen…)"
              value={form.unit}
              onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
              className="border border-stone-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            {isOwner && (
              <input
                placeholder="Price per unit"
                inputMode="decimal"
                value={form.unit_price}
                onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))}
                className="border border-stone-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            )}
            <input
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className={cn(
                'border border-stone-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400',
                isOwner ? '' : 'col-span-2'
              )}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.item_name.trim()}
              className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white rounded text-xs font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              <Check size={12} />
              {saving ? 'Saving…' : editingId ? 'Update' : 'Add'}
            </button>
            <button onClick={cancelAdd} className="px-3 py-1.5 text-xs text-stone-500 hover:bg-stone-100 rounded transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {items.length === 0 && !adding && (
        <p className="px-4 py-4 text-xs text-stone-400 italic">
          No catalog items yet — add what this supplier can provide.
        </p>
      )}

      {/* Items list */}
      {items.length > 0 && (
        <div className="divide-y divide-stone-50">
          {/* Procured items */}
          {procured.length > 0 && (
            <>
              <div className="px-4 pt-2.5 pb-1">
                <span className="text-2xs font-bold uppercase tracking-widest text-green-600 flex items-center gap-1">
                  <ShoppingBag size={10} /> We Procure
                </span>
              </div>
              {procured.map(item => (
                <CatalogItemRow
                  key={item.id}
                  item={item}
                  isOwner={isOwner}
                  supplierId={supplierId}
                  onEdit={() => startEdit(item)}
                  onDelete={() => del({ id: item.id, supplierId })}
                  onToggle={() => toggleProcured(item)}
                />
              ))}
            </>
          )}

          {/* Available (not procuring) */}
          {available.length > 0 && (
            <>
              <div className="px-4 pt-2.5 pb-1">
                <span className="text-2xs font-bold uppercase tracking-widest text-stone-400">
                  Available from Supplier
                </span>
              </div>
              {available.map(item => (
                <CatalogItemRow
                  key={item.id}
                  item={item}
                  isOwner={isOwner}
                  supplierId={supplierId}
                  onEdit={() => startEdit(item)}
                  onDelete={() => del({ id: item.id, supplierId })}
                  onToggle={() => toggleProcured(item)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function CatalogItemRow({
  item, isOwner, supplierId, onEdit, onDelete, onToggle,
}: {
  item: SupplierCatalogItem
  isOwner: boolean
  supplierId: string
  onEdit: () => void
  onDelete: () => void
  onToggle: () => void
}) {
  return (
    <div className={cn(
      'flex items-center gap-2 px-4 py-2 group transition-colors',
      item.is_procured ? 'hover:bg-green-50/40' : 'hover:bg-stone-50'
    )}>
      {/* Procure toggle */}
      <button
        onClick={onToggle}
        title={item.is_procured ? 'We procure this — click to unmark' : 'Mark as procured from this supplier'}
        className={cn(
          'w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
          item.is_procured
            ? 'bg-green-500 border-green-500 text-white'
            : 'border-stone-300 hover:border-green-400'
        )}
      >
        {item.is_procured && <Check size={9} />}
      </button>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <span className={cn('text-sm font-medium', item.is_procured ? 'text-stone-900' : 'text-stone-600')}>
          {item.item_name}
        </span>
        <span className="text-xs text-stone-400 ml-2">{item.unit}</span>
        {item.notes && <span className="text-xs text-stone-400 ml-2 italic">{item.notes}</span>}
      </div>

      {/* Price — owner only */}
      {isOwner && item.unit_price != null && (
        <span className="text-xs font-semibold text-stone-600 bg-stone-100 px-2 py-0.5 rounded shrink-0">
          ${item.unit_price.toFixed(2)}
        </span>
      )}

      {/* Actions — visible on hover */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button onClick={onEdit} className="p-1 rounded text-stone-400 hover:text-brand-600 hover:bg-brand-50 transition-colors">
          <Pencil size={11} />
        </button>
        <button onClick={onDelete} className="p-1 rounded text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors">
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  )
}

// ── Supplier card ─────────────────────────────────────────────────────────────

function SupplierCard({
  supplier,
  linkedIngredients,
  isOwner,
  catalogCount,
}: {
  supplier: Supplier
  linkedIngredients: IngredientWithLinks[]
  isOwner: boolean
  catalogCount: number
}) {
  const { mutateAsync: save } = useSaveSupplier()
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [showCatalog, setShowCatalog] = useState(false)

  const ingredients = linkedIngredients.filter(i => i.item_type === 'ingredient')
  const materials   = linkedIngredients.filter(i => i.item_type === 'material')

  // Rate for a specific ingredient from this supplier
  function getRate(ing: IngredientWithLinks): number | null {
    const junctionEntry = ing.ingredient_suppliers.find(is => is.supplier_id === supplier.id)
    if (junctionEntry?.unit_price != null) return junctionEntry.unit_price
    if (ing.supplier_id === supplier.id && ing.cost_per_unit != null) return ing.cost_per_unit
    return null
  }

  const showItems = linkedIngredients.slice(0, expanded ? undefined : 4)
  const hasMore = linkedIngredients.length > 4

  if (editing) {
    return (
      <SupplierForm
        initial={supplier}
        onDone={() => setEditing(false)}
        onCancel={() => setEditing(false)}
      />
    )
  }

  return (
    <div className={[
      'bg-white rounded-xl border space-y-0 transition-opacity overflow-hidden',
      supplier.is_active ? 'border-stone-200' : 'border-stone-200 opacity-60',
    ].join(' ')}>

      {/* Header */}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-stone-900">{supplier.name}</h3>
              {!supplier.is_active && (
                <span className="text-2xs font-medium text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">Inactive</span>
              )}
            </div>
            {supplier.contact_name && (
              <div className="flex items-center gap-1 mt-0.5 text-xs text-stone-500">
                <User size={11} />
                {supplier.contact_name}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 rounded text-stone-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              title="Edit"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => save({ id: supplier.id, is_active: !supplier.is_active })}
              className={[
                'p-1.5 rounded transition-colors',
                supplier.is_active
                  ? 'text-stone-400 hover:text-danger hover:bg-red-50'
                  : 'text-stone-400 hover:text-success-dark hover:bg-green-50',
              ].join(' ')}
              title={supplier.is_active ? 'Deactivate' : 'Reactivate'}
            >
              {supplier.is_active ? <PowerOff size={13} /> : <Power size={13} />}
            </button>
          </div>
        </div>

        {/* Contact */}
        <div className="space-y-1">
          {supplier.phone && (
            <a
              href={`tel:${supplier.phone}`}
              className="flex items-center gap-2 text-sm text-stone-600 hover:text-brand-700 transition-colors"
            >
              <Phone size={13} className="text-stone-400 shrink-0" />
              {supplier.phone}
            </a>
          )}
          {supplier.email && (
            <a
              href={`mailto:${supplier.email}`}
              className="flex items-center gap-2 text-sm text-stone-600 hover:text-brand-700 transition-colors"
            >
              <Mail size={13} className="text-stone-400 shrink-0" />
              <span className="truncate">{supplier.email}</span>
            </a>
          )}
        </div>

        {/* Summary counts */}
        <div className="flex items-center gap-3 text-2xs text-stone-400">
          {ingredients.length > 0 && (
            <span className="flex items-center gap-1">
              <Package size={11} />
              {ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''}
            </span>
          )}
          {materials.length > 0 && (
            <span className="flex items-center gap-1">
              <Package size={11} />
              {materials.length} material{materials.length !== 1 ? 's' : ''}
            </span>
          )}
          {linkedIngredients.length === 0 && (
            <span className="italic">No items linked yet</span>
          )}
        </div>
      </div>

      {/* Items list */}
      {linkedIngredients.length > 0 && (
        <div className="border-t border-stone-100">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="px-4 py-2 text-left font-semibold text-stone-400 uppercase tracking-wider">Item</th>
                <th className="px-3 py-2 text-left font-semibold text-stone-400 uppercase tracking-wider">Unit</th>
                {isOwner && (
                  <th className="px-3 py-2 text-right font-semibold text-stone-400 uppercase tracking-wider">Our Rate</th>
                )}
                <th className="px-3 py-2 text-left font-semibold text-stone-400 uppercase tracking-wider">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {showItems.map(ing => {
                const rate = getRate(ing)
                return (
                  <tr key={ing.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-stone-800">{ing.name}</td>
                    <td className="px-3 py-2.5 text-stone-500">{ing.unit}</td>
                    {isOwner && (
                      <td className="px-3 py-2.5 text-right font-semibold text-stone-700">
                        {rate != null ? `$${rate.toFixed(2)}` : <span className="text-stone-300">—</span>}
                      </td>
                    )}
                    <td className="px-3 py-2.5">
                      <span className={[
                        'px-1.5 py-0.5 rounded text-2xs font-medium',
                        ing.item_type === 'ingredient'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-blue-50 text-blue-700',
                      ].join(' ')}>
                        {ing.item_type === 'ingredient' ? 'Ingredient' : 'Material'}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {hasMore && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="w-full py-2 text-2xs text-stone-400 hover:text-stone-600 hover:bg-stone-50 transition-colors border-t border-stone-100"
            >
              {expanded ? 'Show less' : `Show ${linkedIngredients.length - 4} more items`}
            </button>
          )}
        </div>
      )}

      {/* Catalog toggle */}
      <button
        onClick={() => setShowCatalog(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 border-t border-stone-100 bg-stone-50/60 hover:bg-stone-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={13} className="text-stone-400" />
          <span className="text-xs font-semibold text-stone-500">Catalog</span>
          {catalogCount > 0 && (
            <span className="text-2xs text-stone-400">{catalogCount} item{catalogCount !== 1 ? 's' : ''}</span>
          )}
        </div>
        <ChevronDown size={13} className={cn('text-stone-400 transition-transform', showCatalog && 'rotate-180')} />
      </button>
      {showCatalog && <CatalogSection supplierId={supplier.id} isOwner={isOwner} />}
    </div>
  )
}

export function SuppliersPage() {
  const { data: suppliers = [], isLoading: suppliersLoading } = useSuppliers()
  const { data: allIngredients = [], isLoading: ingredientsLoading } = useIngredients()
  const { data: allCatalog = [] } = useAllSupplierCatalog()
  const role = useAuthStore(s => s.user?.role)
  const isOwner = role === 'owner'

  const [showForm, setShowForm]     = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [search, setSearch]         = useState('')

  if (suppliersLoading || ingredientsLoading) return <PageSpinner />

  const active   = suppliers.filter(s => s.is_active)
  const inactive = suppliers.filter(s => !s.is_active)
  const shown    = showInactive ? suppliers : active

  const catalogCountBySupplier = allCatalog.reduce((acc, item) => {
    acc[item.supplier_id] = (acc[item.supplier_id] ?? 0) + 1
    return acc
  }, {} as Record<string, number>)

  function getLinkedIngredients(supplierId: string) {
    return (allIngredients as IngredientWithLinks[]).filter(ing =>
      ing.supplier_id === supplierId ||
      ing.ingredient_suppliers?.some(is => is.supplier_id === supplierId)
    )
  }

  // Cross-supplier search: group catalog matches by normalised item name
  const trimmed = search.trim().toLowerCase()
  const searchMatches = trimmed
    ? allCatalog.filter(c => c.item_name.toLowerCase().includes(trimmed))
    : []

  // Group matches: Map<normalisedName, items[]>
  const searchGroups = searchMatches.reduce((acc, item) => {
    const key = item.item_name.trim().toLowerCase()
    if (!acc.has(key)) acc.set(key, [])
    acc.get(key)!.push(item)
    return acc
  }, new Map<string, typeof searchMatches>())

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Suppliers</h1>
          <p className="text-sm text-stone-400 mt-0.5">{active.length} active supplier{active.length !== 1 ? 's' : ''}</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus size={14} />
            Add Supplier
          </button>
        )}
      </div>

      {showForm && (
        <SupplierForm
          initial={BLANK}
          onDone={() => setShowForm(false)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Search across all catalog items */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
        <input
          type="search"
          placeholder="Search items across all suppliers… (e.g. Jalebi)"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Cross-supplier search results */}
      {trimmed && (
        <div className="space-y-3">
          {searchGroups.size === 0 ? (
            <p className="text-sm text-stone-400 text-center py-8">
              No catalog items match "{search}" — try adding it to a supplier's catalog.
            </p>
          ) : (
            <>
              <p className="text-xs text-stone-400">
                Found <span className="font-semibold text-stone-600">{searchMatches.length}</span> result{searchMatches.length !== 1 ? 's' : ''} across{' '}
                <span className="font-semibold text-stone-600">{searchGroups.size}</span> item{searchGroups.size !== 1 ? 's' : ''}
              </p>
              {Array.from(searchGroups.entries()).map(([, items]) => {
                const displayName = items[0].item_name
                const procuredFrom = items.filter(i => i.is_procured)
                return (
                  <div key={displayName} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b border-stone-100">
                      <h3 className="font-semibold text-stone-900">{displayName}</h3>
                      <div className="flex items-center gap-2">
                        {procuredFrom.length > 0 && (
                          <span className="text-2xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            Procuring from {procuredFrom.length}
                          </span>
                        )}
                        <span className="text-2xs text-stone-400">{items.length} supplier{items.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-stone-50">
                      {items
                        .sort((a, b) => (b.is_procured ? 1 : 0) - (a.is_procured ? 1 : 0))
                        .map(item => (
                          <div key={item.id} className={cn(
                            'flex items-center gap-3 px-4 py-3',
                            item.is_procured ? 'bg-green-50/30' : ''
                          )}>
                            <div className={cn(
                              'w-2 h-2 rounded-full shrink-0',
                              item.is_procured ? 'bg-green-500' : 'bg-stone-300'
                            )} />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-stone-800">
                                {item.supplier?.name ?? '—'}
                              </span>
                              {item.unit && (
                                <span className="text-xs text-stone-400 ml-2">per {item.unit}</span>
                              )}
                              {item.notes && (
                                <span className="text-xs text-stone-400 ml-2 italic">{item.notes}</span>
                              )}
                            </div>
                            {item.is_procured && (
                              <span className="text-2xs font-semibold text-green-700 shrink-0">We procure ✓</span>
                            )}
                            {isOwner && item.unit_price != null && (
                              <span className="text-sm font-bold text-stone-700 shrink-0">
                                ${item.unit_price.toFixed(2)}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}

      {!isOwner && !trimmed && (
        <p className="text-xs text-stone-400 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
          Supplier rates are visible to owners only.
        </p>
      )}

      {!trimmed && (
        <div className="grid grid-cols-1 gap-3">
          {shown.map(s => (
            <SupplierCard
              key={s.id}
              supplier={s}
              linkedIngredients={getLinkedIngredients(s.id)}
              isOwner={isOwner}
              catalogCount={catalogCountBySupplier[s.id] ?? 0}
            />
          ))}
        </div>
      )}

      {shown.length === 0 && !showForm && (
        <div className="text-center py-16">
          <p className="text-stone-400 mb-4">No suppliers yet.</p>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors mx-auto"
          >
            <Plus size={14} />
            Add your first supplier
          </button>
        </div>
      )}

      {inactive.length > 0 && (
        <button
          onClick={() => setShowInactive(v => !v)}
          className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
        >
          {showInactive ? `Hide ${inactive.length} inactive` : `Show ${inactive.length} inactive supplier${inactive.length !== 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  )
}
